import React, { useContext, useState } from "react"
import {
  IconUser,
  IconSettings,
  IconMessageHeart,
  IconHelp,
  IconLogin,
  IconUserPlus,
  IconDeviceGamepad2,
  IconBackpack,
  IconWand,
  IconSparkles,
  IconArrowLeft,
} from "@tabler/icons-react"
import { CurrentUserContext } from "@love-irish/application-session-provider"
import { FeedbackModal } from "@love-irish/application-feedback-modal"
import { useMaybeSuggestionContext } from "@love-irish/suggestion-targets"
import { useFloatingMenu } from "./context"
import routes from "@love-irish/routes"

interface MenuItemProps {
  icon: React.ReactNode
  label: string
  onClick?: () => void
  href?: string
}

const menuItems = {
  help: () => (
    <MenuItem icon={<IconHelp size={18} />} label="Help" href="/help" />
  ),
  arcade: () => (
    <MenuItem
      icon={<IconDeviceGamepad2 size={18} />}
      label="Play games to earn EXP"
      href={routes.arcadePath()}
    />
  ),
  customizeProfile: (handleMenuClick) => (
    <MenuItem
      icon={<IconUser size={18} />}
      label="Customize Profile"
      onClick={handleMenuClick}
    />
  ),
  settings: () => (
    <MenuItem
      icon={<IconSettings size={18} />}
      label="Settings"
      href={routes.settingsProfilePath()}
    />
  ),
  signUp: () => (
    <MenuItem
      icon={<IconUserPlus size={18} />}
      label="Sign Up"
      href={routes.signupPath()}
    />
  ),
  login: () => (
    <MenuItem icon={<IconLogin size={18} />} label="Log In" href={routes.loginPath()} />
  ),
  feedback: () => <FeedbackMenuItem />,
  inventory: () => (
    <MenuItem
      icon={<IconBackpack size={18} />}
      label="View inventory"
      href={routes.inventoryPath()}
    />
  ),
}

function MenuItem({ icon, label, onClick, href }: MenuItemProps) {
  const className =
    "flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors w-full text-left"

  if (href) {
    return (
      <a href={href} className={className}>
        {icon}
        <span>{label}</span>
      </a>
    )
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {icon}
      <span>{label}</span>
    </button>
  )
}

function FeedbackMenuItem() {
  return (
    <FeedbackModal
      trigger={
        <button
          type="button"
          className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors w-full text-left"
        >
          <IconMessageHeart size={18} />
          <span>Share feedback ❤️</span>
        </button>
      }
    />
  )
}

interface FloatingMenuProps {
  onClose: () => void
}

export function FloatingMenu({ onClose }: FloatingMenuProps) {
  const currentUser = useContext(CurrentUserContext)
  const isLoggedIn = !!currentUser?.id
  const { openProfileEditModal } = useFloatingMenu()
  const [view, setView] = useState<"root" | "workflows">("root")
  // The app-shell mounts a SuggestionTargetProvider on every page, so this is present app-wide. "Help me
  // write" reads it to arm pick mode; it's optional-chained so the entry is safe even without a provider.
  const suggestions = useMaybeSuggestionContext()

  const handleEditProfile = () => {
    onClose()
    openProfileEditModal()
  }

  // Workflows submenu.
  if (view === "workflows") {
    return (
      <div>
        <button
          type="button"
          onClick={() => setView("root")}
          className="mb-1 flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          <IconArrowLeft size={14} /> Workflows
        </button>
        <MenuItem
          icon={<IconSparkles size={18} className="text-violet-500" />}
          label="Help me write…"
          onClick={() => {
            suggestions?.setPickMode(true)
            onClose()
          }}
        />
      </div>
    )
  }

  // Always show Workflows -> Help me write: it's a global entry point, not gated on whether a pickable field
  // happens to be mounted right now. Pick mode + section dots then guide the user to the fields.
  const workflowsEntry = (
    <>
      <div className="border-t border-gray-100 my-2" />
      <MenuItem
        icon={<IconWand size={18} className="text-violet-500" />}
        label="Workflows"
        onClick={() => setView("workflows")}
      />
    </>
  )

  if (isLoggedIn) {
    return (
      <div className="">
        {menuItems.inventory()}
        {menuItems.arcade()}
        {menuItems.customizeProfile(handleEditProfile)}
        {workflowsEntry}
        <div className="border-t border-gray-100 my-2" />
        {menuItems.feedback()}
        {menuItems.help()}
      </div>
    )
  }

  return (
    <div className="">
      {menuItems.signUp()}
      {menuItems.login()}
      <div className="border-t border-gray-100 my-2" />
      {menuItems.arcade()}
      {workflowsEntry}
      <div className="border-t border-gray-100 my-2" />
      {menuItems.feedback()}
      {menuItems.help()}
    </div>
  )
}

export default FloatingMenu
